import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/admin/Table";
import { SchoolService } from "../../services/SchoolService";

const schoolService = new SchoolService();

export default function Schools() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allSchoolsActive, setAllSchoolsActive] = useState(false);
  const [filters, setFilters] = useState({
    schoolName: "",
    neighborhoodName: "",
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, action: null, message: "" });

  const fetchSchools = () => {
    setLoading(true);
    schoolService
      .find({ itemsPerPage: 300, active: "all", pagination: false })
      .then((response) => {
        setAllSchools(response.items);
        setSchools(response.items);
        setAllSchoolsActive(response.items.every((school) => school.active));
      })
      .catch((error) => {
        console.error("Erro ao buscar escolas:", error);
        alert("Não foi possível carregar as escolas e eventos.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setIsFiltering(true);

    let filteredSchools = [...allSchools];

    if (filters.schoolName && filters.schoolName.trim() !== "") {
      filteredSchools = filteredSchools.filter((school) =>
        school.name.toLowerCase().includes(filters.schoolName.toLowerCase())
      );
    }

    if (filters.neighborhoodName && filters.neighborhoodName.trim() !== "") {
      filteredSchools = filteredSchools.filter((school) =>
        school.address?.neighborhood?.toLowerCase().includes(filters.neighborhoodName.toLowerCase())
      );
    }

    setSchools(filteredSchools);
    setTimeout(() => setIsFiltering(false), 300);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      schoolName: "",
      neighborhoodName: "",
    });
    setSchools(allSchools);
  };

  const confirmAction = (action, message) => {
    setConfirmModal({ show: true, action, message });
  };

  const executeConfirmedAction = async () => {
    const { action } = confirmModal;
    setConfirmModal({ show: false, action: null, message: "" });
    if (action) await action();
  };

  const handleDisableAll = () => {
    confirmAction(
      async () => {
        try {
          await schoolService.toggleAllSchoolsStatus(false);
          setAllSchoolsActive(false);
          fetchSchools();
        } catch (error) {
          console.error("Erro ao desativar escolas:", error);
          alert("Não foi possível desativar as escolas.");
        }
      },
      "Tem certeza que deseja DESATIVAR todas as escolas? (Eventos não serão afetados)"
    );
  };

  const handleEnableAll = () => {
    confirmAction(
      async () => {
        try {
          await schoolService.toggleAllSchoolsStatus(true);
          setAllSchoolsActive(true);
          fetchSchools();
        } catch (error) {
          console.error("Erro ao ativar escolas:", error);
          alert("Não foi possível ativar as escolas.");
        }
      },
      "Tem certeza que deseja ATIVAR todas as escolas? (Eventos não serão afetados)"
    );
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const toggleSchoolStatus = async (school) => {
    try {
      if (school.active) {
        await schoolService.disable(school._id);
      } else {
        await schoolService.enable(school._id);
      }
      fetchSchools();
    } catch (error) {
      console.error("Erro ao alterar status da escola:", error);
      alert("Não foi possível alterar o status da escola.");
    }
  };

  function renderSwitch(school) {
    return (
      <div className="form-check form-switch text-center">
        <input
          className="form-check-input"
          type="checkbox"
          checked={school.active}
          onChange={() => toggleSchoolStatus(school)}
        />
      </div>
    );
  }

  function renderType(school) {
    const type = school.type || "ESCOLA";
    const isEvent = type === "EVENTO";
    return (
      <span className={`badge ${isEvent ? "bg-info text-dark" : "bg-secondary"}`}>
        {type}
      </span>
    );
  }

  const columns = [
    {
      label: "Ativo",
      field: "active",
      render: renderSwitch,
    },
    {
      label: "Tipo",
      field: "type",
      render: renderType,
    },
    { label: "Nome", field: "name" },
    { label: "Rua", field: "address.street" },
    { label: "Numero", field: "address.number" },
    { label: "Bairro", field: "address.neighborhood" },
    { label: "Cidade", field: "address.city" },
    { label: "CEP", field: "address.zipCode" },
  ];

  const onAdd = () => navigate("/admin/schools/new");
  const onEdit = (school) => navigate(`/admin/schools/${school._id}`);
  const onDelete = (school) => {
    confirmAction(
      () =>
        schoolService
          .delete(school._id)
          .then(() => {
            alert("Escola excluída com sucesso!");
            fetchSchools();
          })
          .catch((error) => {
            console.error("Erro ao excluir escola:", error);
            alert("Não foi possível excluir a escola.");
          }),
      `Tem certeza de que deseja excluir "${school.name}"?`
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-5">Carregando escolas e eventos...</div>
    );
  }

  return (
    <div>
      <div className="container pt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Escolas / Eventos</h2>
          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={handleEnableAll}
            >
              Ativar todas as escolas
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDisableAll}
            >
              Desativar todas as escolas
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="card mb-4" style={{ border: "none", width: "100%" }}>
          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label fw-bold">Nome da Escola</label>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Bairro</label>
              </div>
              <div className="col-md-4"></div>
            </div>

            <div className="row align-items-end">
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome da escola"
                  value={filters.schoolName}
                  onChange={(e) =>
                    handleFilterChange("schoolName", e.target.value)
                  }
                  onKeyPress={handleKeyPress}
                />
              </div>

              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome do bairro"
                  value={filters.neighborhoodName}
                  onChange={(e) =>
                    handleFilterChange("neighborhoodName", e.target.value)
                  }
                  onKeyPress={handleKeyPress}
                />
              </div>

              <div className="col-md-4">
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                    onClick={handleApplyFilters}
                    disabled={isFiltering}
                  >
                    Filtrar
                  </button>

                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                    onClick={handleClearFilters}
                  >
                    Limpar Filtros
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={schools}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        className="mt-5"
      />

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <>
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmação</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setConfirmModal({ show: false, action: null, message: "" })
                    }
                  />
                </div>
                <div className="modal-body">
                  <p>{confirmModal.message}</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                      setConfirmModal({ show: false, action: null, message: "" })
                    }
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={executeConfirmedAction}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
